import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Loader2, Users } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    http
      .get(`/team/invites/${token}/info`)
      .then(({ data }) => alive && setInfo(data))
      .catch((e) => alive && setErr(formatApiErrorDetail(e.response?.data?.detail) || "Invite not found"));
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      const { data } = await http.post("/team/invites/accept", {
        token,
        name: name.trim() || undefined,
        password: pw,
      });
      if (data?.token) localStorage.setItem("liq_token", data.token);
      if (refresh) await refresh();
      toast.success("Welcome to the team");
      nav("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not accept invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-ink-50 px-6 py-16">
      <div className="max-w-md w-full bg-white border border-ink-100 p-10" data-testid="accept-invite-page">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src="/logo-128.png" alt="LeadIQ Pro" width="40" height="40" className="w-10 h-10" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-base tracking-tight text-ink-900">
              LeadIQ Pro
            </div>
            <div className="text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
              Team invite
            </div>
          </div>
        </Link>

        {err ? (
          <div data-testid="invite-error">
            <h1 className="font-display text-2xl font-extrabold text-ink-900 mb-3">Invite unavailable</h1>
            <p className="text-ink-500 text-sm mb-6">{err}</p>
            <Link to="/login" className="text-sm text-brand hover:text-brand-hover font-semibold underline-offset-4 hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : !info ? (
          <div className="text-sm text-ink-500 flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading invite…
          </div>
        ) : (
          <>
            <div className="w-12 h-12 grid place-items-center bg-ink-900 text-white mb-6">
              <Users className="w-6 h-6" />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink-900 leading-tight mb-3">
              Join the team.
            </h1>
            <p className="text-ink-500 text-sm mb-2 leading-relaxed">
              <span className="font-mono text-ink-900">{info.invited_by_email}</span> invited you to LeadIQ Pro.
            </p>
            <p className="text-ink-400 text-xs mb-8 leading-relaxed">
              Accepting as <span className="font-mono text-ink-900">{info.email}</span>
            </p>

            <form onSubmit={submit} className="space-y-5" data-testid="accept-invite-form">
              <div>
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  Your name (optional)
                </Label>
                <Input
                  data-testid="invite-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Chen"
                  className="mt-2 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  Choose a password
                </Label>
                <Input
                  data-testid="invite-password-input"
                  type="password"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="••••••••"
                  className="mt-2 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11"
                />
              </div>
              <Button
                data-testid="invite-submit-button"
                type="submit"
                disabled={busy || !pw}
                className="w-full h-11 rounded-sm bg-brand hover:bg-brand-hover text-white font-medium tracking-wide group"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Accept & join <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>)}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
